/**
 * A member's clinic setup as one entry per clinic, read from and written back
 * to the member's row. The clinic cells (Clinics, Clinic roles, Disciplines,
 * Employee type) and the per-clinic dialog each change one part of it and
 * save the whole thing in one request.
 */

import type { DisciplineType } from '../../../services/api/models/DisciplineType';
import type { EmployeeType } from '../../../services/api/models/EmployeeType';
import type {
  ClinicSetupItem,
  DisciplinesSource,
  RoleResponse,
} from '../../../services/api/types.gen';
import { DISCIPLINE_ORDER } from '../../../constants/disciplines';
import { employeeTypeLabel } from '../../../constants/employeeTypes';
import {
  CLINIC_CLINICIAN_ROLE_SLUG,
  isAssignableClinicianRole,
} from '../../../utils/rbacRoles';
import type { OrgMemberResponse } from '../types';

export type ClinicSetupEntry = {
  clinic_id: string;
  /**
   * Every clinic role the member holds here, including ones the grid does not
   * show (an approver role, a custom one), so a save keeps them.
   */
  role_ids: string[];
  clinician: boolean;
  caseManager: boolean;
  clinicalManager: boolean;
  disciplines: DisciplineType[];
  /**
   * DERIVED: the server works the disciplines out from the member's titles,
   * so a new clinic's are left for it to fill in.
   */
  disciplines_source: DisciplinesSource | null;
  employee_type: EmployeeType | null;
  employee_type_other: string | null;
};

export type ClinicRoles = Pick<
  ClinicSetupEntry,
  'clinician' | 'caseManager' | 'clinicalManager'
>;

export type Employment = Pick<
  ClinicSetupEntry,
  'employee_type' | 'employee_type_other'
>;

/** The three clinic roles the grid edits, by their slugs in the catalogue. */
export const CLINIC_ROLE_SLUGS: Record<keyof ClinicRoles, string> = {
  clinician: CLINIC_CLINICIAN_ROLE_SLUG,
  caseManager: 'clinic:case-manager',
  clinicalManager: 'clinic:clinical-manager',
};

/** The permission behind each flag, read off the member's clinic roles. */
const FLAG_PERMISSIONS: Record<'caseManager' | 'clinicalManager', string> = {
  caseManager: 'clinic:assignable-case-manager',
  clinicalManager: 'clinic:assignable-clinical-manager',
};

/** The role id behind each of the three, read from the agency's catalogue. */
export type ClinicRoleIds = Record<keyof ClinicRoles, string>;

/** The disciplines checklist's options, in the order the grid shows them. */
export const DISCIPLINE_OPTIONS = DISCIPLINE_ORDER.map(d => ({
  value: d,
  label: d,
}));

export const CLINIC_ROLE_OPTIONS: {
  value: keyof ClinicRoles;
  label: string;
}[] = [
  { value: 'clinician', label: 'Clinician' },
  { value: 'caseManager', label: 'Case Manager' },
  { value: 'clinicalManager', label: 'Clinical Manager' },
];

/** The title a member's dialogs use: their name, or their email if none. */
export function memberDisplayName(
  member: Pick<OrgMemberResponse, 'first_name' | 'last_name' | 'email'>
): string {
  return (
    [member.first_name?.trim(), member.last_name?.trim()]
      .filter(Boolean)
      .join(' ') || member.email
  );
}

/**
 * The member's clinics within the operator's bound, as entries; every clinic
 * when the bound is null (not loaded, or the operator cannot edit).
 */
export function setupEntries(
  member: Pick<OrgMemberResponse, 'clinic_access' | 'clinic_assignments'>,
  boundIds: ReadonlySet<string> | null
): ClinicSetupEntry[] {
  const byClinic = new Map(
    (member.clinic_assignments ?? []).map(a => [a.clinic_id, a])
  );
  return (member.clinic_access ?? [])
    .filter(access => !boundIds || boundIds.has(access.clinic_id))
    .map(access => {
      const a = byClinic.get(access.clinic_id);
      return {
        clinic_id: access.clinic_id,
        // Access lists every live clinic role, including ones that grant no
        // permission, and the attributes stored here; the assignment is
        // missing for a clinic with only those roles.
        role_ids: [...(access.role_ids ?? a?.role_ids ?? [])],
        clinician: !!a?.is_assignable_clinician,
        caseManager: !!a?.is_case_manager,
        clinicalManager: !!a?.is_clinical_manager,
        disciplines: [...(access.disciplines ?? a?.disciplines ?? [])],
        disciplines_source:
          access.disciplines_source ?? a?.disciplines_source ?? null,
        employee_type: access.employee_type ?? a?.employee_type ?? null,
        employee_type_other:
          access.employee_type_other ?? a?.employee_type_other ?? null,
      };
    });
}

/**
 * The role a flag is held through when it is not the grid's own role for it:
 * its name, "Another role" when the catalogue does not say, or null when the
 * flag is the grid's to change. Such a flag stays ticked: taking the grid's
 * role away would not clear it.
 */
export function heldThrough(
  entry: ClinicSetupEntry,
  key: keyof ClinicRoles,
  roleIds: ClinicRoleIds,
  catalogue: readonly RoleResponse[]
): string | null {
  if (!entry[key] || entry.role_ids.includes(roleIds[key])) return null;
  const grants = (role: RoleResponse) =>
    key === 'clinician'
      ? isAssignableClinicianRole(role)
      : (role.permissions ?? []).some(p => p.slug === FLAG_PERMISSIONS[key]);
  return (
    catalogue.find(role => entry.role_ids.includes(role.id) && grants(role))
      ?.name ?? 'Another role'
  );
}

/**
 * The entry with these clinic roles. Only a role that changes is granted or
 * taken away, so a member who is a clinician through some other role is not
 * given a second one, and a flag held through another role stays.
 */
export function withRoles(
  entry: ClinicSetupEntry,
  roles: ClinicRoles,
  roleIds: ClinicRoleIds
): ClinicSetupEntry {
  let ids = [...entry.role_ids];
  const next: ClinicRoles = { ...roles };
  for (const key of Object.keys(CLINIC_ROLE_SLUGS) as (keyof ClinicRoles)[]) {
    if (roles[key] === entry[key]) continue;
    const id = roleIds[key];
    if (roles[key]) {
      if (!ids.includes(id)) ids = [...ids, id];
    } else if (ids.includes(id)) {
      ids = ids.filter(held => held !== id);
    } else {
      // Held through another role: unticking here changes nothing.
      next[key] = true;
    }
  }
  return { ...entry, ...next, role_ids: ids };
}

/**
 * Only the employee type and its text: what an employment control may change,
 * so a draft held while "Other" is typed cannot carry roles or disciplines.
 */
export function employmentOf(value: Employment): Employment {
  return {
    employee_type: value.employee_type,
    employee_type_other: value.employee_type_other,
  };
}

/** "Other" needs saying what. */
export function needsOtherText(type: EmployeeType | null): boolean {
  return type === 'OTHER';
}

/** The employment with this type; its "Other" text only where it applies. */
export function withEmployment<T extends Employment>(
  value: T,
  type: EmployeeType | null
): T {
  return {
    ...value,
    employee_type: type,
    employee_type_other: needsOtherText(type)
      ? value.employee_type_other
      : null,
  };
}

/** The entry with disciplines an operator chose. */
export function withDisciplines(
  entry: ClinicSetupEntry,
  disciplines: DisciplineType[]
): ClinicSetupEntry {
  return { ...entry, disciplines, disciplines_source: 'OVERRIDE' };
}

/**
 * A clinic just ticked for somebody: the clinic roles and employment their
 * other clinics share, or nothing in particular for their first. Disciplines
 * are copied when their clinics agree; otherwise the server works them out.
 * Only the grid's own roles are granted, so the ticks match what is sent.
 */
export function newClinicEntry(
  entries: ClinicSetupEntry[],
  clinicId: string,
  roleIds: ClinicRoleIds | null
): ClinicSetupEntry {
  const roles = roleIds ? uniformRoles(entries) : null;
  const disciplines = uniformDisciplines(entries);
  const employment = uniformEmployment(entries);
  const derived =
    disciplines === null ||
    (entries.length > 0 &&
      entries.every(e => e.disciplines_source === 'DERIVED'));
  const keys = Object.keys(CLINIC_ROLE_SLUGS) as (keyof ClinicRoles)[];
  return {
    clinic_id: clinicId,
    role_ids:
      roles && roleIds ? keys.filter(k => roles[k]).map(k => roleIds[k]) : [],
    clinician: roles?.clinician ?? false,
    caseManager: roles?.caseManager ?? false,
    clinicalManager: roles?.clinicalManager ?? false,
    disciplines: disciplines ? [...disciplines] : [],
    disciplines_source: derived ? 'DERIVED' : null,
    employee_type: employment?.employee_type ?? null,
    employee_type_other: employment?.employee_type_other ?? null,
  };
}

/**
 * The member as the list will show them once the entries are saved, for the
 * optimistic overlay. Clinics outside the bound are kept as they are.
 */
export function memberWithEntries(
  member: OrgMemberResponse,
  entries: ClinicSetupEntry[],
  boundIds: ReadonlySet<string>,
  clinicName: (id: string) => string
): Pick<OrgMemberResponse, 'clinic_access' | 'clinic_assignments'> {
  const assignments = member.clinic_assignments ?? [];
  return {
    clinic_access: [
      ...(member.clinic_access ?? []).filter(a => !boundIds.has(a.clinic_id)),
      ...entries.map(e => ({
        clinic_id: e.clinic_id,
        clinic_name: clinicName(e.clinic_id),
        role_ids: e.role_ids,
        disciplines: e.disciplines,
        disciplines_source: e.disciplines_source,
        employee_type: e.employee_type,
        employee_type_other: e.employee_type_other,
      })),
    ],
    clinic_assignments: [
      ...assignments.filter(a => !boundIds.has(a.clinic_id)),
      ...entries.map(e => {
        const before = assignments.find(a => a.clinic_id === e.clinic_id);
        return {
          clinic_id: e.clinic_id,
          clinic_name: clinicName(e.clinic_id),
          is_assignable_clinician: e.clinician,
          is_case_manager: e.caseManager,
          is_clinical_manager: e.clinicalManager,
          is_approver: before?.is_approver ?? false,
          role_ids: e.role_ids,
          disciplines: e.disciplines,
          employee_type: e.employee_type,
          employee_type_other: e.employee_type_other,
          disciplines_source: e.disciplines_source,
        };
      }),
    ],
  };
}

function sameDisciplines(a: DisciplineType[], b: DisciplineType[]): boolean {
  return a.length === b.length && a.every(d => b.includes(d));
}

function sameRoleIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every(id => b.includes(id));
}

export function sameEmployment(a: Employment, b: Employment): boolean {
  return (
    a.employee_type === b.employee_type &&
    (!needsOtherText(a.employee_type) ||
      (a.employee_type_other ?? '') === (b.employee_type_other ?? ''))
  );
}

/** Whether two setups would save the same thing. */
export function sameSetup(
  a: ClinicSetupEntry[],
  b: ClinicSetupEntry[]
): boolean {
  return (
    a.length === b.length &&
    a.every(x => {
      const y = b.find(e => e.clinic_id === x.clinic_id);
      return (
        !!y &&
        sameRoleIds(x.role_ids, y.role_ids) &&
        x.clinician === y.clinician &&
        x.caseManager === y.caseManager &&
        x.clinicalManager === y.clinicalManager &&
        sameDisciplines(x.disciplines, y.disciplines) &&
        sameEmployment(x, y)
      );
    })
  );
}

/**
 * The request body for `entries`, against the setup the server last
 * confirmed (`base`). Every clinic carries its full role list. At a clinic
 * already in `base`, disciplines and employee type go only where they
 * changed, so disciplines nobody touched stay derived from the member's
 * titles. A clinic not in `base` gets exactly what the grid shows: its
 * employee type (null clears one left from before) and, for a clinician, its
 * disciplines unless the server is to derive them.
 */
export function clinicSetupBody(
  base: ClinicSetupEntry[],
  entries: ClinicSetupEntry[]
): ClinicSetupItem[] {
  return entries.map(entry => {
    const before = base.find(b => b.clinic_id === entry.clinic_id);
    const item: ClinicSetupItem = {
      clinic_id: entry.clinic_id,
      role_ids: entry.role_ids,
    };
    if (
      before
        ? !sameDisciplines(before.disciplines, entry.disciplines)
        : entry.clinician && entry.disciplines_source !== 'DERIVED'
    ) {
      item.disciplines = entry.disciplines;
    }
    if (!before || !sameEmployment(before, entry)) {
      item.employee_type = entry.employee_type;
      item.employee_type_other = needsOtherText(entry.employee_type)
        ? entry.employee_type_other
        : null;
    }
    return item;
  });
}

/** The clinic roles every clinic shares, or null when they differ. */
export function uniformRoles(entries: ClinicSetupEntry[]): ClinicRoles | null {
  const [first] = entries;
  if (!first) return null;
  return entries.every(
    e =>
      e.clinician === first.clinician &&
      e.caseManager === first.caseManager &&
      e.clinicalManager === first.clinicalManager
  )
    ? first
    : null;
}

/**
 * The disciplines shared by every clinic they are a clinician at: a list,
 * null when those clinics differ, undefined when they are a clinician nowhere.
 */
export function uniformDisciplines(
  entries: ClinicSetupEntry[]
): DisciplineType[] | null | undefined {
  const clinician = entries.filter(e => e.clinician);
  const [first] = clinician;
  if (!first) return undefined;
  return clinician.every(e => sameDisciplines(e.disciplines, first.disciplines))
    ? first.disciplines
    : null;
}

/** The employment every clinic shares, or null when they differ. */
export function uniformEmployment(
  entries: ClinicSetupEntry[]
): Employment | null {
  const [first] = entries;
  if (!first) return null;
  return entries.every(e => sameEmployment(e, first)) ? first : null;
}

/**
 * The most senior clinic role and a count of the rest: "Clinical Manager +2";
 * '' for none.
 */
export function rolesText(value: ClinicRoles): string {
  const held = [
    value.clinicalManager && 'Clinical Manager',
    value.caseManager && 'Case Manager',
    value.clinician && 'Clinician',
  ].filter((role): role is string => !!role);
  if (!held.length) return '';
  return held.length > 1 ? `${held[0]} +${held.length - 1}` : held[0];
}

export function employmentText(value: Employment): string {
  return employeeTypeLabel(value.employee_type, value.employee_type_other);
}
